import { polygon as createPolygon, difference, featureCollection } from "@turf/turf";
import type { XY } from "@designboticteam/core";
import { toClosedPolygon } from "./toClosedPolygon";

const AREA_EPSILON_M2 = 0.001;

function planarRingArea(ring: ReadonlyArray<XY>): number {
	if (ring.length < 3) {
		return 0;
	}

	let twiceArea = 0;

	for (let i = 0; i < ring.length - 1; i++) {
		twiceArea += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
	}

	return Math.abs(twiceArea) / 2;
}

// Checks if inner is inside outer on the x/z plane (touching edges count as inside).
// Uses Turf's `difference` — if nothing is left over, inner fits. Shoelace area is used
// because Turf's `area` would assume WGS84. Assumes y = 0.
function isPolygonInsidePolygon(innerPolygon: Array<XY>, outerPolygon: Array<XY>): boolean {
	const innerRing = toClosedPolygon(innerPolygon);
	const outerRing = toClosedPolygon(outerPolygon);

	if (innerRing === null || outerRing === null) {
		return false;
	}

	const diff = difference(featureCollection([createPolygon([innerRing]), createPolygon([outerRing])]));

	if (diff === null) {
		return true;
	}

	const polygons = diff.geometry.type === "Polygon" ? [diff.geometry.coordinates] : diff.geometry.coordinates;

	let totalArea = 0;

	for (const poly of polygons) {
		const [outerHole, ...holes] = poly;
		totalArea += planarRingArea(outerHole as Array<XY>);

		for (const hole of holes) {
			totalArea -= planarRingArea(hole as Array<XY>);
		}
	}

	return totalArea < AREA_EPSILON_M2;
}

export { isPolygonInsidePolygon };
